import React, { useState, useEffect } from 'react';
import { getAllStudents, setUserActive, createStudentByAdmin } from '../services/userService';
import Toast from './Toast';
import './UserManagement.css';

const AddStudentModal = ({ onClose, onCreated }) => {
  const [form, setForm] = useState({ firstName: '', lastName: '', username: '', email: '', password: '' });
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName || !form.lastName || !form.username || !form.email || !form.password) {
      setError('All fields are required.');
      return;
    }
    setIsSubmitting(true);
    try {
      const { error: createError } = await createStudentByAdmin(form);
      if (createError) {
        const errorMsg = createError.message || '';
        if (errorMsg.includes('Failed to send') || errorMsg.includes('FunctionsFetchError')) {
          setError('The student creation service is not yet active. Ask your system administrator to deploy the admin-create-user Edge Function.');
        } else {
          setError(errorMsg || 'Failed to create student.');
        }
        return;
      }
      onCreated();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-card" onClick={(e) => e.stopPropagation()}>
        <h3>Add New Student</h3>
        {error && <div className="error-message">{error}</div>}
        <form onSubmit={handleSubmit}>
          {[
            { name: 'firstName', label: 'First Name', type: 'text' },
            { name: 'lastName', label: 'Last Name', type: 'text' },
            { name: 'username', label: 'Username', type: 'text' },
            { name: 'email', label: 'Email', type: 'email' },
            { name: 'password', label: 'Password', type: 'password' },
          ].map(({ name, label, type }) => (
            <div className="form-group" key={name}>
              <label htmlFor={name}>{label}</label>
              <input
                type={type}
                id={name}
                name={name}
                className="form-control"
                value={form[name]}
                onChange={handleChange}
                required
              />
            </div>
          ))}
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="action-btn" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create Student'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [toast, setToast] = useState(null);

  const loadUsers = async () => {
    setIsLoading(true);
    setError('');
    const { data, error: fetchError } = await getAllStudents();
    if (fetchError) {
      setError('Failed to load users. Please try again.');
    } else {
      setUsers(data || []);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const toggleUserStatus = async (userId, currentStatus) => {
    // Optimistic update
    setUsers((prev) =>
      prev.map((u) => (u.id === userId ? { ...u, is_active: !currentStatus } : u))
    );
    const { error: updateError } = await setUserActive(userId, !currentStatus);
    if (updateError) {
      // Revert on failure
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, is_active: currentStatus } : u))
      );
      setToast({ message: 'Failed to update student status. Please try again.', type: 'error' });
    } else {
      setToast({
        message: currentStatus ? 'Student deactivated successfully.' : 'Student activated successfully.',
        type: 'success',
      });
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="user-management">
      <div className="user-management-header">
        <h1>User Management</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search users..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <i className="fas fa-search search-icon"></i>
          </div>
          <button className="action-btn" onClick={() => setShowAddModal(true)}>
            + Add Student
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {isLoading ? (
        <p style={{ textAlign: 'center', padding: '2rem' }}>Loading users...</p>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>First Name</th>
                <th>Last Name</th>
                <th>Username</th>
                <th>Email</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="6" style={{ textAlign: 'center', padding: '1.5rem' }}>
                    No students found.
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr key={user.id}>
                    <td>{user.first_name}</td>
                    <td>{user.last_name}</td>
                    <td>{user.username}</td>
                    <td>{user.email}</td>
                    <td>
                      <span className={`status-badge ${user.is_active ? 'active' : 'inactive'}`}>
                        {user.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <button
                        className="action-btn"
                        onClick={() => toggleUserStatus(user.id, user.is_active)}
                      >
                        {user.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showAddModal && (
        <AddStudentModal
          onClose={() => setShowAddModal(false)}
          onCreated={() => {
            setShowAddModal(false);
            loadUsers();
            setToast({ message: 'Student account created successfully.', type: 'success' });
          }}
        />
      )}

      {toast && <Toast message={toast.message} type={toast.type} onDismiss={() => setToast(null)} />}
    </div>
  );
};

export default UserManagement;
